/**
 * List marker styling for rich text surfaces.
 *
 * Plain string helpers with no imports, so any surface can use them without
 * pulling Jodit into its bundle. Same rationale as ./sanitize.ts.
 *
 * The marker cycles by nesting depth the way Docs/Notion-style editors do, so a
 * nested list is visually distinguishable from its parent. email_service carries
 * a parallel copy of these cycles for the delivered email — keep them in sync.
 */

/**
 * Wrapper class on each row of the combined list dropdown, set by
 * listsControl.ts's childTemplate and laid out in RichTextEditor.styles.tsx.
 * Declared here so the styles module doesn't have to import the control module
 * (which pulls in Jodit).
 */
export const LISTS_OPTION_CLASS = 'joy-lists-option';

/**
 * Added to an editor container only when that surface has opted into lists.
 * RichTextEditorGlobalStyle is a global stylesheet shared by every editor
 * instance, so the list rules hang off this class rather than `.jodit-editor` —
 * otherwise the ~15 surfaces that inherit the default toolbar would start
 * showing markers and indentation for pasted lists that their own render
 * targets (e.g. the guest site) still don't style, trading one
 * editor-vs-output mismatch for another.
 */
export const LISTS_ENABLED_CLASS = 'joy-rich-text-lists';

/** disc -> circle -> square, repeating. */
const UL_MARKERS: ReadonlyArray<string> = ['disc', 'circle', 'square'];

/** 1. -> a. -> i., repeating. */
const OL_MARKERS: ReadonlyArray<string> = ['decimal', 'lower-alpha', 'lower-roman'];

/**
 * Levels to emit. Two full cycles; a list nested deeper than this keeps the
 * last emitted marker rather than resetting, which is the lesser evil and
 * far past any realistic email.
 */
const MAX_DEPTH = 6;

/**
 * Emits `list-style-type` rules for each nesting depth.
 *
 * @param scope selector the rules hang off — a class for a global stylesheet, or
 *   `&` inside a styled-component.
 * @returns CSS text
 */
export const listMarkerCycleCss = (scope: string): string =>
  (
    [
      ['ul', UL_MARKERS],
      ['ol', OL_MARKERS],
    ] as ReadonlyArray<readonly [string, ReadonlyArray<string>]>
  )
    .flatMap(([tag, markers]) =>
      Array.from({ length: MAX_DEPTH }, (_unused, index) => {
        const nesting = Array.from({ length: index + 1 }, () => tag).join(' ');
        return `${scope} ${nesting} { list-style-type: ${markers[index % markers.length]}; }`;
      }),
    )
    .join('\n  ');
